import { jwtDecode } from "jwt-decode";

import { localStorageKeys } from "../../config";
import { PlatformAPI } from "../../utils/platform";
import { clearUserIdCookie, setUserIdCookie } from "../../utils/userIdCookie";
import { LogSentryException, LogSentryMessage } from "../sentryLogger/sentryLogger";

const noIFrameSupportBrowserNames = ["Safari", "Mobile Safari"];

export type AuthResponseData = {
  id: string;
  email: string;
  refreshToken: string;
  accessToken: string;
  emailToken?: string;
};

export const getUserIdFromRefreshToken: () => string | null = () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }
  try {
    const decodedToken: { sub: string } = jwtDecode(refreshToken);
    if (decodedToken && decodedToken.sub) {
      return decodedToken.sub;
    }
  } catch (error) {
    LogSentryException({ messagePrefix: "getUserIdFromRefreshToken", error });
    return null;
  }

  return null;
};

export const isLoggedIn = (): boolean => {
  return getRefreshToken() !== null;
};

export const getAccessToken = (): string | null =>
  window.localStorage.getItem(localStorageKeys.accessToken);

export const getRefreshToken = (): string | null =>
  window.localStorage.getItem(localStorageKeys.refreshToken);

export const removeTokens = () => {
  window.localStorage.removeItem(localStorageKeys.refreshToken);
  window.localStorage.removeItem(localStorageKeys.accessToken);
  clearUserIdCookie();
};

export const setTokens = (refreshToken: string, accessToken: string) => {
  window.localStorage.setItem(localStorageKeys.refreshToken, refreshToken);
  window.localStorage.setItem(localStorageKeys.accessToken, accessToken);
  const userId = getUserIdFromRefreshToken();
  if (userId) {
    setUserIdCookie(userId);
  }
};

export const CrossWebAppRequest = ({
  url,
  expectedPostMessageEventData,
  timeout = 5000,
}: {
  url: URL;
  expectedPostMessageEventData: Parameters<typeof postMessage>[0];
  timeout?: number;
}) => {
  const timeoutObject = (() => {
    let timeoutID: number;
    const promise: Promise<void> = new Promise<void>((resolve) => {
      timeoutID = window.setTimeout(() => {
        LogSentryMessage({ message: "CrossWebAppRequest timeout" });
        resolve();
      }, timeout);
    });

    const clear = () => {
      clearTimeout(timeoutID);
    };
    return { promise, clear };
  })();

  const eventPromise = new Promise<void>((resolve) => {
    addEventListener("message", (event) => {
      if (event.origin !== url.origin) {
        return;
      }

      if (event.data === expectedPostMessageEventData) {
        timeoutObject.clear();
        LogSentryMessage({ message: "CrossWebAppRequest finished" });
        resolve();
      }
    });
  });

  let iframe: HTMLIFrameElement | undefined;
  let newWindow: Window | null = null;

  // For safari we need to open a new tab, because the localstorage changes are not propagated from the iframe
  if (noIFrameSupportBrowserNames.includes(PlatformAPI.getInstance().getBrowserName() ?? "")) {
    newWindow = window.open("about:blank", "_blank");
    if (!newWindow) {
      LogSentryMessage({ message: "CrossWebAppRequest new window blocked" });
      timeoutObject.clear();
      return;
    }
    newWindow.blur();
    window.focus();
    newWindow.location.href = url.href;
  } else {
    iframe = document.createElement("iframe");
    iframe.setAttribute("src", url.href);
    iframe.setAttribute("style", "width:0;height:0;border:0;border:none;");
    document.body.appendChild(iframe);
  }

  return Promise.race([eventPromise, timeoutObject.promise]).then(() => {
    if (iframe) {
      document.body.removeChild(iframe);
    }

    if (newWindow) {
      newWindow.close();
    }
  });
};
